import React, { useEffect, useRef } from 'react';
import Quote from '../components/Quote';
import { Link } from 'react-router-dom';
import { SiteContent } from '../content';

const About: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, { threshold: 0.01, rootMargin: "0px 0px -100px 0px" });

    const elements = containerRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach(el => observer.observe(el));

    // Force visibility fallback for elements already in view
    const timeoutId = setTimeout(() => {
      elements?.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight) el.classList.add('is-visible');
      });
    }, 500);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, []);

  const { hero, why, reframe, community, closing } = SiteContent.about;

  return (
    <div ref={containerRef} className="flex flex-col w-full bg-paper overflow-hidden">
      {/* SECTION 1: HERO */}
      <section className="relative min-h-[60vh] md:h-[80vh] flex items-center justify-center overflow-hidden py-32 md:py-40 bg-rich-black">
        <img
          src="https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?auto=format&fit=crop&q=80&w=2400"
          alt="Outdoor table with soil"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 text-center max-w-5xl px-6">
          <div className="animate-on-scroll animate-fade-up">
            <Quote large text={hero.text} className="!text-[#F5F0E6] drop-shadow-xl" />
          </div>
        </div>
      </section>

      {/* SECTION 2: THE WHY */}
      <section className="section-padding bg-paper relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div>
            <div className="animate-on-scroll animate-fade-up">
               <h2 className="section-title text-4xl md:text-5xl lg:text-7xl mb-8 leading-none">{why.title}</h2>
            </div>
            <div className="animate-on-scroll animate-fade-up delay-200">
               <p className="text-lg font-medium text-[#1A1A1A]/80 leading-relaxed mb-8 max-w-lg">
               {why.text}
               </p>
            </div>
            <div className="animate-on-scroll animate-fade-up delay-300">
               <Quote text={why.quote} className="!text-left px-0" />
            </div>
          </div>
          <div className="relative animate-on-scroll animate-mask img-placeholder aspect-[4/3] rounded-xl overflow-hidden shadow-2xl">
            <img src="https://images.unsplash.com/photo-1459156212016-c812468e2115?auto=format&fit=crop&q=80&w=1200" alt="Grounded" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* SECTION 3: THE REFRAME */}
      <section className="py-24 md:py-40 bg-rich-black relative">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="animate-on-scroll animate-fade-up">
            <Quote large text={reframe.quote} className="mb-12" />
          </div>
          <div className="animate-on-scroll animate-fade-up delay-200">
            <p className="text-[#F5F0E6]/70 text-xl leading-relaxed italic max-w-4xl mx-auto">
              {reframe.text}
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4: COMMUNITY */}
      <section className="section-padding bg-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-on-scroll animate-mask img-placeholder aspect-square rounded-xl overflow-hidden shadow-2xl">
             <img src="https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?auto=format&fit=crop&q=80&w=1200" alt="Hands in soil" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="animate-on-scroll animate-fade-up">
               <h2 className="section-title text-4xl md:text-5xl lg:text-7xl mb-8 leading-none">{community.title}</h2>
            </div>
            <div className="animate-on-scroll animate-fade-up delay-200">
               <p className="text-lg font-medium text-[#1A1A1A]/80 leading-relaxed mb-10">
               {community.text}
               </p>
            </div>
            <div className="animate-on-scroll animate-fade-up delay-300">
               <p className="text-sm font-black text-[#A65D3F] uppercase tracking-widest italic">{community.accent}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: CLOSING */}
      <section className="section-padding bg-rich-black text-center">
        <div className="max-w-4xl mx-auto px-6">
          <div className="animate-on-scroll animate-fade-up">
            <Quote large text={closing.quote} className="mb-10" />
            <p className="text-white/60 mb-16 text-xl italic">{closing.description}</p>
          </div>
          <div className="pt-4 animate-on-scroll animate-scale-in delay-200">
            <Link to="/shop" className="btn-primary !px-12">Shop the Alphabet</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;